import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import BrandFooter from './BrandFooter';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiShield, FiClock, FiArrowRight, FiPhone, FiMail } = FiIcons;

const FinalCTA = () => {
  useEffect(() => {
    // Redirect to the external website after a short delay
    const timer = setTimeout(() => {
      window.location.href = 'https://ethos.burbankinsuranceservices.com';
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const benefits = [
    {
      icon: FiClock,
      title: "5-Minute Application",
      description: "Quick and easy online process"
    },
    {
      icon: FiShield,
      title: "No Medical Exam",
      description: "Just answer a few health questions"
    },
    {
      icon: FiCheckCircle,
      title: "Instant Approval",
      description: "Get covered immediately"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white flex items-center justify-center">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        
        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
            <SafeIcon icon={FiCheckCircle} className="w-12 h-12 text-white" />
          </div>

          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            You're Almost Protected!
          </h1>
          
          <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Complete your application with our trusted partner to get the coverage 
            that's perfect for your family's needs.
          </p>

          {/* Redirect Message */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8"
          >
            <div className="flex items-center justify-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400 mr-3"></div>
              <span className="text-lg font-semibold">Redirecting you to secure application...</span>
            </div>
            <p className="text-blue-200">
              You'll be taken to our secure partner site in a few seconds
            </p>
          </motion.div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1, duration: 0.6 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6"
              >
                <SafeIcon icon={benefit.icon} className="w-8 h-8 mx-auto mb-4 text-yellow-400" />
                <h3 className="font-bold text-lg mb-2">{benefit.title}</h3>
                <p className="text-blue-200 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Manual CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8"
          >
            <h3 className="text-2xl font-bold mb-4">
              Not redirected automatically?
            </h3>
            <p className="text-blue-200 mb-6">
              Click the button below to continue to your secure application
            </p>
            
            <a
              href="https://ethos.burbankinsuranceservices.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-primary-900 font-bold py-4 px-8 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl mb-6"
            >
              Continue to Application
              <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </a>

            {/* Contact Info */}
            <div className="pt-6 border-t border-white/20">
              <p className="text-blue-200 mb-4">Need help? Contact our licensed agents:</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="tel:+1234567890" 
                  className="flex items-center text-yellow-400 hover:text-yellow-300 transition-colors"
                >
                  <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2" />
                  (123) 456-7890
                </a>
                <a 
                  href="mailto:info@burbankinsuranceservices.com" 
                  className="flex items-center text-yellow-400 hover:text-yellow-300 transition-colors"
                >
                  <SafeIcon icon={FiMail} className="w-4 h-4 mr-2" />
                  Get Help
                </a>
              </div>
            </div>
          </motion.div>

          {/* Brand Footer */}
          <BrandFooter variant="dark" />
        </motion.div>
      </div>
    </div>
  );
};

export default FinalCTA;